import { SERVICE_URL } from "@app/config/getServiceUrl";
import { getCookie } from "@base/lib/utils";
import { useEffect, useRef, useState } from "react";
import { useRefreshLockMutation } from "./useDailyLogLockMutations";

const HEARTBEAT_INTERVAL_MS = 60_000;
const FAILURE_WARNING_THRESHOLD = 2;
const CSRF_TOKEN_COOKIE_NAME = "csrf_token";

const getLockUrl = (projectId: string, logId: string) =>
  `${SERVICE_URL}/ko/project-management/api/projects/${projectId}/daily-logs/${logId}/lock`;

function releaseLockWithKeepalive(lockUrl: string) {
  return fetch(lockUrl, {
    method: "DELETE",
    keepalive: true,
    credentials: "include",
    headers: { "X-CSRFToken": getCookie(CSRF_TOKEN_COOKIE_NAME) ?? "" },
  });
}

export function useLockManager(projectId: string, logId: string) {
  const [isConnectionUnstable, setIsConnectionUnstable] = useState(false);
  const failureCountRef = useRef(0);

  const { mutate: refreshLock } = useRefreshLockMutation(projectId, logId, {
    onSuccess: () => {
      failureCountRef.current = 0;
      setIsConnectionUnstable(false);
    },
    onError: () => {
      failureCountRef.current += 1;
      if (failureCountRef.current >= FAILURE_WARNING_THRESHOLD) {
        setIsConnectionUnstable(true);
      }
    },
  });

  const releaseLock = () => {
    releaseLockWithKeepalive(getLockUrl(projectId, logId));
  };

  // 60초 heartbeat
  useEffect(() => {
    const interval = setInterval(() => {
      refreshLock();
    }, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [refreshLock]);

  // foreground 복귀 시 즉시 refresh
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshLock();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [refreshLock]);

  // 페이지 종료 시 best-effort 해제 (fetch keepalive)
  useEffect(() => {
    const lockUrl = getLockUrl(projectId, logId);

    const handleUnload = () => {
      releaseLockWithKeepalive(lockUrl);
    };

    window.addEventListener("beforeunload", handleUnload);
    window.addEventListener("pagehide", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
      window.removeEventListener("pagehide", handleUnload);
    };
  }, [projectId, logId]);

  return { isConnectionUnstable, releaseLock };
}
